import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, MapPin, ArrowLeft, Download, Eye, Heart, FileText, Info } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { EvenementsService, type Evenement } from '../../services/evenements.service';
import { getImageUrl } from '../../services/api';
import CountdownTimer from '../../components/common/CountdownTimer';
import logo from '../../assets/logo.png';

const EvenementDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [event, setEvent] = useState<Evenement | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'annonce' | 'programme'>('annonce');

    useEffect(() => {
        if (id) {
            loadEvent(id);
        }
    }, [id]);

    const loadEvent = async (eventId: string) => {
        try {
            setLoading(true);
            setError(null);
            const data = await EvenementsService.findOne(eventId);
            setEvent(data);

            // Increment view count
            try {
                await EvenementsService.incrementView(eventId);
            } catch (err) {
                console.error('Failed to increment view', err);
            }
        } catch (err) {
            console.error('Failed to load event', err);
            setError("Impossible de charger l'événement. Il a peut-être été supprimé.");
        } finally {
            setLoading(false);
        }
    };

    const handleLike = async () => {
        if (!event) return;
        try {
            const updatedEvent = await EvenementsService.incrementLike(event.id);
            setEvent(prev => prev ? { ...prev, nombreLikes: updatedEvent.nombreLikes } : null);
        } catch (err) {
            console.error('Failed to like event', err);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-white pt-20 flex justify-center items-center">
                <div className="flex flex-col items-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
                    <p className="text-gray-600">Chargement de l'événement...</p>
                </div>
            </div>
        );
    }

    if (error || !event) {
        return (
            <div className="min-h-screen bg-white pt-20 flex justify-center items-center">
                <div className="text-center max-w-lg px-4">
                    <h2 className="text-2xl font-bold text-gray-800 mb-4">Événement introuvable</h2>
                    <p className="text-gray-600 mb-6">{error || "Cet événement n'existe pas ou a été supprimé."}</p>
                    <button
                        onClick={() => navigate('/Evenement')}
                        className="px-6 py-2 bg-primary text-white rounded-full hover:bg-primary/90 transition-colors inline-flex items-center"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Retour aux événements
                    </button>
                </div>
            </div>
        );
    }

    const dateDebut = new Date(event.dateDebut);
    const dateFin = new Date(event.dateFin);

    const formatDate = (date: Date) => {
        return date.toLocaleDateString('fr-FR', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="min-h-screen bg-white pt-20 pb-12">
            <Helmet>
                <title>{event.titre} - SAPI</title>
            </Helmet>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header Section */}
                <div className="mb-8">
                    <div className="flex justify-between items-start mb-4">
                        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight max-w-4xl">
                            {event.titre}
                        </h1>
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                            <div className="flex items-center">
                                <Eye className="w-4 h-4 mr-1" />
                                <span>{event.nombreVues}</span>
                            </div>
                            <button onClick={handleLike} className="flex items-center hover:text-rose-500 transition-colors">
                                <Heart className="w-4 h-4 mr-1" />
                                <span>{event.nombreLikes}</span>
                            </button>
                        </div>
                    </div>

                    <div className="text-gray-500 font-medium text-lg flex items-center">
                        <Calendar className="w-5 h-5 mr-2" />
                        <span>{formatDate(dateDebut)} - {formatDate(dateFin)}</span>
                    </div>
                </div>

                {/* Countdown Section */}
                <div className="mb-12">
                    <CountdownTimer targetDate={event.isCallForPapers ? event.dateSoumission : event.dateDebut} />
                </div>

                {/* Tabs Navigation */}
                <div className="bg-secondary/5 rounded-t-lg border-b border-secondary/20 mb-8">
                    <div className="flex">
                        <button
                            onClick={() => setActiveTab('annonce')}
                            className={`flex items-center px-6 py-4 font-semibold text-lg transition-colors relative ${activeTab === 'annonce'
                                ? 'text-primary-dark bg-white rounded-t-lg shadow-sm'
                                : 'text-gray-500 hover:text-primary'
                                }`}
                        >
                            <Info className="w-5 h-5 mr-2" />
                            Annonce
                            {activeTab === 'annonce' && (
                                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary to-secondary rounded-t-lg" />
                            )}
                        </button>
                        <button
                            onClick={() => setActiveTab('programme')}
                            className={`flex items-center px-6 py-4 font-semibold text-lg transition-colors relative ${activeTab === 'programme'
                                ? 'text-primary-dark bg-white rounded-t-lg shadow-sm'
                                : 'text-gray-500 hover:text-primary'
                                }`}
                        >
                            <FileText className="w-5 h-5 mr-2" />
                            Programme
                            {activeTab === 'programme' && (
                                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary to-secondary rounded-t-lg" />
                            )}
                        </button>
                    </div>
                </div>

                {/* Content Section */}
                <motion.div
                    key={activeTab}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                >
                    {activeTab === 'annonce' && (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                            {/* Left Column: Description */}
                            <div className="lg:col-span-2">
                                <div className="bg-gradient-to-r from-primary/5 to-secondary/5 p-6 rounded-2xl mb-8 flex items-center border border-secondary/10 shadow-sm">
                                    <img src={logo} alt="SAPI Logo" className="h-16 w-auto mr-6 object-contain" />
                                    <div>
                                        <h3 className="font-bold text-primary-dark text-lg">SAPI</h3>
                                        <p className="text-gray-600">Société Algérienne de l'industrie pharmaceutique</p>
                                    </div>
                                </div>

                                <div className="prose prose-lg text-gray-700 max-w-none whitespace-pre-wrap break-words overflow-hidden">
                                    {event.description || "Aucune description disponible."}
                                </div>

                                {event.lienInscription && event.lienInscription.trim() !== '' && event.lienInscription !== 'null' && event.lienInscription !== 'undefined' && (
                                    <div className="mt-10">
                                        <a
                                            href={event.lienInscription}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center px-8 py-4 bg-highlight text-white rounded-full font-bold text-lg hover:bg-highlight-dark transition-all duration-300 shadow-md hover:shadow-xl transform hover:-translate-y-1"
                                        >
                                            S'inscrire à l'événement
                                        </a>
                                    </div>
                                )}
                            </div>

                            {/* Right Column: Image */}
                            <div className="lg:col-span-1">
                                <div className="sticky top-24">
                                    <div className="bg-white p-2 rounded-xl shadow-lg border border-gray-100">
                                        {event.image ? (
                                            <img
                                                src={getImageUrl(event.image)}
                                                alt={event.titre}
                                                className="w-full h-auto rounded-lg"
                                            />
                                        ) : (
                                            <div className="w-full aspect-[3/4] bg-gray-100 rounded-lg flex items-center justify-center">
                                                <Calendar className="w-20 h-20 text-gray-300" />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'programme' && (
                        <div className="bg-white rounded-xl p-4 md:p-8 min-h-[600px] flex flex-col items-center justify-center border border-gray-100 shadow-sm">
                            {event.pdfProgramme ? (
                                <div className="w-full h-full flex flex-col">
                                    <div className="flex justify-between items-center mb-6">
                                        <h3 className="text-xl font-bold text-gray-800">Programme de l'événement</h3>
                                        <a
                                            href={getImageUrl(event.pdfProgramme)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors flex items-center shadow-lg"
                                        >
                                            <Download className="w-4 h-4 mr-2" />
                                            Télécharger le PDF
                                        </a>
                                    </div>
                                    <iframe
                                        src={`${getImageUrl(event.pdfProgramme)}#view=FitH`}
                                        className="w-full h-[800px] rounded-lg border border-gray-200 bg-white"
                                        title="Programme PDF"
                                    />
                                </div>
                            ) : (
                                <div className="text-center">
                                    <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Pas de programme disponible</h3>
                                    <p className="text-gray-500">Le programme détaillé n'a pas encore été ajouté pour cet événement.</p>
                                </div>
                            )}
                        </div>
                    )}
                </motion.div>
            </div>
        </div>
    );
};

export default EvenementDetail;
